import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';
import { getDeviceInfo } from '../lib/utils';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('token');
        const savedUser = localStorage.getItem('user');
        
        if (token && savedUser) {
            try {
                setUser(JSON.parse(savedUser));
            } catch (e) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
            }
            // Refresh user & permissions from the server (permissions may have changed)
            authAPI.getMe()
                .then(res => {
                    const fresh = res.data?.user || res.data;
                    if (fresh && fresh.username) {
                        localStorage.setItem('user', JSON.stringify(fresh));
                        setUser(fresh);
                    }
                })
                .catch(() => {});
        }
        setLoading(false);
    }, []);

    const login = async (username, password) => {
        const response = await authAPI.login({ username, password, ...getDeviceInfo() });
        const { token, user: userData } = response.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        return userData;
    };

    const register = async (username, password, name) => {
        const response = await authAPI.register({ username, password, name });
        const { token, user: userData } = response.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
    };

    // Vérifier si l'utilisateur a une permission spécifique
    const hasPermission = useCallback((permission) => {
        if (!user) return false;
        // Les admins ont toutes les permissions
        if (user.role === 'admin') return true;
        // Vérifier dans la liste des permissions de l'utilisateur
        const permissions = user.permissions || [];
        return permissions.includes(permission);
    }, [user]);

    // Vérifier plusieurs permissions (toutes requises)
    const hasAllPermissions = useCallback((permissionList) => {
        return permissionList.every(p => hasPermission(p));
    }, [hasPermission]);

    // Vérifier si l'utilisateur a au moins une des permissions
    const hasAnyPermission = useCallback((permissionList) => {
        return permissionList.some(p => hasPermission(p));
    }, [hasPermission]);

    return (
        <AuthContext.Provider value={{ 
            user, 
            login, 
            register, 
            logout, 
            loading,
            hasPermission,
            hasAllPermissions,
            hasAnyPermission
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
